import { LanguageType } from "types/index";

export type TJapaneseEraType = "showa" | "heisei" | "reiwa" | "western";

export interface IJapaneseEra {
  name: string;
  nameJa: string;
  abbreviation: string;
  abbreviationJa: string;
  startYear: number;
  startMonth: number;
  startDay: number;
  endYear?: number;
  endMonth?: number;
  endDay?: number;
}

export const JAPANESE_ERAS: Record<TJapaneseEraType, IJapaneseEra> = {
  showa: {
    name: "Showa",
    nameJa: "昭和",
    abbreviation: "S",
    abbreviationJa: "昭",
    startYear: 1926,
    startMonth: 12,
    startDay: 25,
    endYear: 1989,
    endMonth: 1,
    endDay: 7,
  },
  heisei: {
    name: "Heisei",
    nameJa: "平成",
    abbreviation: "H",
    abbreviationJa: "平",
    startYear: 1989,
    startMonth: 1,
    startDay: 8,
    endYear: 2019,
    endMonth: 4,
    endDay: 30,
  },
  reiwa: {
    name: "Reiwa",
    nameJa: "令和",
    abbreviation: "R",
    abbreviationJa: "令",
    startYear: 2019,
    startMonth: 5,
    startDay: 1,
  },
  western: {
    name: "Western",
    nameJa: "西暦",
    abbreviation: "W",
    abbreviationJa: "西",
    startYear: 1,
    startMonth: 1,
    startDay: 1,
  },
};

/**
 * Convert a Western date to Japanese era information
 * @param date - The date to convert
 * @returns The Japanese era type and year within that era
 */
export const getJapaneseEra = (
  date: Date
): { era: TJapaneseEraType; year: number; eraInfo: IJapaneseEra } | null => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // Check Reiwa (2019/5/1 - present)
  if (
    year > JAPANESE_ERAS.reiwa.startYear ||
    (year === JAPANESE_ERAS.reiwa.startYear &&
      (month > JAPANESE_ERAS.reiwa.startMonth ||
        (month === JAPANESE_ERAS.reiwa.startMonth &&
          day >= JAPANESE_ERAS.reiwa.startDay)))
  ) {
    return {
      era: "reiwa",
      year: year - JAPANESE_ERAS.reiwa.startYear + 1,
      eraInfo: JAPANESE_ERAS.reiwa,
    };
  }

  // Check Heisei (1989/1/8 - 2019/4/30)
  if (
    year > JAPANESE_ERAS.heisei.startYear ||
    (year === JAPANESE_ERAS.heisei.startYear &&
      (month > JAPANESE_ERAS.heisei.startMonth ||
        (month === JAPANESE_ERAS.heisei.startMonth &&
          day >= JAPANESE_ERAS.heisei.startDay)))
  ) {
    return {
      era: "heisei",
      year: year - JAPANESE_ERAS.heisei.startYear + 1,
      eraInfo: JAPANESE_ERAS.heisei,
    };
  }

  // Check Showa (1926/12/25 - 1989/1/7)
  if (
    year > JAPANESE_ERAS.showa.startYear ||
    (year === JAPANESE_ERAS.showa.startYear &&
      (month > JAPANESE_ERAS.showa.startMonth ||
        (month === JAPANESE_ERAS.showa.startMonth &&
          day >= JAPANESE_ERAS.showa.startDay)))
  ) {
    return {
      era: "showa",
      year: year - JAPANESE_ERAS.showa.startYear + 1,
      eraInfo: JAPANESE_ERAS.showa,
    };
  }

  // Default to Western calendar for dates before Showa era
  return {
    era: "western",
    year: year,
    eraInfo: JAPANESE_ERAS.western,
  };
};

/**
 * Format Japanese era notation for display
 * @param date - The date to format
 * @param language - Current language (ja or en)
 * @returns Formatted era string like "(H23)" or "(平23)"
 */
export const formatJapaneseEraNotation = (
  date: Date,
  language: LanguageType
): string => {
  const eraData = getJapaneseEra(date);
  if (!eraData) return "";

  const { year, eraInfo } = eraData;
  const abbreviation =
    language === "ja" ? eraInfo.abbreviationJa : eraInfo.abbreviation;

  return `${abbreviation}${year}`;
};

/**
 * Format Japanese era notation with parentheses
 * @param date - The date to format
 * @param language - Current language (ja or en)
 * @returns Formatted era string with parentheses like "(H23)" or "(平23)"
 */
export const formatJapaneseEraWithParentheses = (
  date: Date,
  language: LanguageType
): string => {
  const notation = formatJapaneseEraNotation(date, language);
  return notation ? `(${notation})` : "";
};

/**
 * Get Japanese era full name
 * @param date - The date
 * @param language - Current language (ja or en)
 * @returns Full era name like "Heisei" or "平成"
 */
export const getJapaneseEraName = (
  date: Date,
  language: LanguageType
): string => {
  const eraData = getJapaneseEra(date);
  if (!eraData) return "";

  return language === "ja" ? eraData.eraInfo.nameJa : eraData.eraInfo.name;
};
